import React, { useEffect, useState } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Article } from '@/slices/blog/mocks/articles'
import { extractHeadings } from '@/slices/blog/utils/generateHeadingId'
import { Box, styled } from '@mui/material'

interface TableOfContentsProps {
  article: Article
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({ article }) => {
  const headings = extractHeadings(article.blocks)
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: '-80px 0px -80% 0px' },
    )

    headings.forEach(({ id }) => {
      const el = document.getElementById(id)

      if (el) {
        observer.observe(el)
      }
    })

    return () => observer.disconnect()
  }, [headings])

  if (headings.length < 3) {
    return null
  }

  return (
    <TocWrapper>
      <TocTitle>In this article</TocTitle>
      <TocList>
        {headings.map(({ id, text }) => (
          <TocListItem key={id} className={activeId === id ? 'active' : ''}>
            <TocLink href={`#${id}`}>{text}</TocLink>
          </TocListItem>
        ))}
      </TocList>
    </TocWrapper>
  )
}

const TocWrapper = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: pxToRem(100),
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto',
  paddingLeft: pxToRem(24),
  borderLeft: `2px solid ${theme.palette.divider}`,

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}))

const TocTitle = styled('p')(({ theme }) => ({
  fontSize: pxToRem(12),
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: theme.palette.text.secondary,
  marginBottom: pxToRem(16),
  margin: 0,
  paddingBottom: pxToRem(16),
}))

const TocList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
})

const TocListItem = styled('li')(({ theme }) => ({
  marginBottom: pxToRem(8),
  transition: 'all 0.2s ease',

  '&.active': {
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
}))

const TocLink = styled('a')(({ theme }) => ({
  fontSize: pxToRem(14),
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  lineHeight: 1.5,
  display: 'block',
  padding: `${pxToRem(4)} 0`,
  transition: 'color 0.2s ease',

  '&:hover': {
    color: theme.palette.primary.main,
  },
}))
